/** @format */
import React from 'react';
import {
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  OutlinedInput,
  TextField,
  ListItemText,
  Checkbox,
  Typography,
  Button,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PulseLoader from 'react-spinners/PulseLoader';
import AlertDialogSlide from './voucherBatch.status.jsx';
import dayjs from 'dayjs';

import { useSelector, useDispatch } from 'react-redux';
import Record from './voucherBatch.customer.jsx';
import {
  setVoucherDate,
  setVoucherSelectedKeys,
  fetchVoucherInvSegKeyStart,
  fetchVoucherBatchCustomersStart,
  sendVoucherEmailStart,
} from '../../redux/voucher/voucher.actions';

import {
  selectVoucherDate,
  selectIsFetching,
  selectVoucherBatchCustomers,
  selectIsSending,
  selectVoucherInvSegKeys,
  selectIsVoucherInvSegKeysEmpty,
  selectVoucherSelectedCustomers,
  selectVoucherEmailStatus,
  selectVoucherSelectedKeys,
} from '../../redux/voucher/voucher.selector';

const themeOptions = {
  palette: {
    type: 'light',
    primary: {
      main: '#b71c1c',
    },
    secondary: {
      main: '#e0e0e0',
    },
    warning: {
      main: '#ff9100',
    },
  },
};
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
const names = [
  'Oliver Hansen',
  'Van Henry',
  'April Tucker',
  'Ralph Hubbard',
  'Omar Alexander',
  'Carlos Abbott',
  'Miriam Wagner',
  'Bradley Wilkerson',
  'Virginia Andrews',
  'Kelly Snyder',
];

function Inputs() {
  const theme = createTheme(deepmerge(themeOptions));
  const dispatch = useDispatch();
  const [date, setDate] = React.useState('');

  const [open, setOpen] = React.useState(false);

  const keys = useSelector(selectVoucherInvSegKeys);

  const isFetching = useSelector(selectIsFetching);

  const isSending = useSelector(selectIsSending);

  const isVoucherInvSegKeysEmpty = useSelector(selectIsVoucherInvSegKeysEmpty);

  const selectedVoucherKeys = useSelector(selectVoucherSelectedKeys);
  const [selectedKeys, setSelectedKeys] = React.useState(selectedVoucherKeys);

  const handleDateChange = (newValue, _ctx) => {
    setDate(newValue);
    dispatch(setVoucherDate(dayjs(newValue).format('YYYY-MM-DD')));
  };

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    let selectedKeys = typeof value === 'string' ? value.split(',') : value;

    dispatch(setVoucherSelectedKeys(selectedKeys));

    // setSelectedKeys(selectedKeys);

    dispatch(fetchVoucherBatchCustomersStart());
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  React.useEffect(() => {
    if (
      !isFetching &&
      !isSending &&
      !isVoucherInvSegKeysEmpty &&
      selectedKeys.length === 0
    ) {
      setOpen(true);
    }
  }, [isFetching, isSending, isVoucherInvSegKeysEmpty]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridColumnGap: '1rem',
        }}
      >
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={date}
              onChange={handleDateChange}
              renderInput={(props) => <TextField {...props} />}
            />
          </LocalizationProvider>
        </>

        <>
          <FormControl fullWidth>
            <InputLabel id="voucher-key-multi-select-label">
              Inventory Segment Keys
            </InputLabel>
            <Select
              labelId="voucher-key-multi-select-label"
              id="voucher-key-multi-select"
              multiple
              value={selectedVoucherKeys}
              onChange={handleChange}
              input={<OutlinedInput label="Inventory Segment Keys" />}
              MenuProps={MenuProps}
              disabled={isFetching || isSending || isVoucherInvSegKeysEmpty}
              onOpen={handleOpen}
              onClose={handleClose}
              open={open}
              renderValue={(selected) =>
                selected
                  .map((id) => keys.find((obj) => obj.id === id)?.key)
                  .join(', ')
              }
            >
              {keys?.map((obj) => (
                <MenuItem key={obj.key} value={obj.id}>
                  <Checkbox
                    checked={selectedVoucherKeys.indexOf(obj.id) > -1}
                  />
                  <ListItemText primary={obj.key} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      </Box>
    </ThemeProvider>
  );
}

function CustomerViz() {
  const customers = useSelector(selectVoucherBatchCustomers);
  const selectedVoucherBatchCustomers = useSelector(
    selectVoucherSelectedCustomers
  );
  const [selectedCustomers, setSelectedCustomers] = React.useState(
    selectedVoucherBatchCustomers
  );
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '100%',
        gridRowGap: 5,
        maxHeight: '500px',
        overflowY: 'auto',
      }}
    >
      {customers?.map((customer) => (
        <Record
          key={customer.voucherNumber}
          voucherId={customer.voucherId}
          voucherIssuanceId={customer.voucherIssuanceId}
          createdAt={customer.createdAt}
          firstName={customer.firstName}
          lastName={customer.lastName}
          voucherAmount={customer.voucherAmount}
          voucherExpirationDate={customer.voucherExpirationDate}
          voucherNumber={customer.voucherNumber}
          selectedCustomers={selectedCustomers}
          setSelectedCustomers={setSelectedCustomers}
        />
      ))}
    </Box>
  );
}

function SendEmailButton() {
  const dispatch = useDispatch();
  const selectedCustomers = useSelector(selectVoucherSelectedCustomers);
  const isSending = useSelector(selectIsSending);
  const handleClick = () => {
    dispatch(sendVoucherEmailStart(selectedCustomers));
  };
  return (
    <Button
      variant="contained"
      color="primary"
      disabled={isSending || selectedCustomers.length === 0}
      onClick={handleClick}
    >
      Send Email
    </Button>
  );
}

function VoucherBatchComponent() {
  const isFetching = useSelector(selectIsFetching);
  const isSending = useSelector(selectIsSending);
  const voucherEmailStatus = useSelector(selectVoucherEmailStatus);

  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (voucherEmailStatus.length > 0) {
      setDialogOpen(true);
    }
    return () => {
      setDialogOpen(false);
    };
  }, [voucherEmailStatus]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr 1fr',
        gridRowGap: '1rem',
        gridColumnGap: '1rem',
      }}
    >
      <AlertDialogSlide open={dialogOpen} setOpen={setDialogOpen} />
      <Box
        sx={{
          display: 'grid',

          justifyItems: 'center',
        }}
      >
        <PulseLoader
          loading={isFetching || isSending}
          color="#b71c1c"
          style={{
            justifySelf: 'center',
          }}
          size={15}
        />
        <Inputs />
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr 1fr 1fr 1fr 1fr 1fr',
          gridColumnGap: '1rem',
          padding: '1rem',
          borderRadius: '0.5rem',
          border: '1px solid #b71c1c',
        }}
      >
        <Typography sx={{ alignSelf: 'center' }}>Voucher ID</Typography>
        <Typography sx={{ alignSelf: 'center' }}>Issuance ID</Typography>
        <Typography sx={{ alignSelf: 'center' }}>Created Date</Typography>
        <Typography sx={{ alignSelf: 'center' }}>Customer Name</Typography>
        <Typography sx={{ alignSelf: 'center' }}>Voucher Amount</Typography>
        <Typography sx={{ alignSelf: 'center' }}>Expiration Date</Typography>
        <Typography sx={{ alignSelf: 'center' }}>Voucher Number</Typography>
      </Box>
      <CustomerViz />
      <SendEmailButton />
    </Box>
  );
}

export default VoucherBatchComponent;
